import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaArrowLeft, FaCheckCircle, FaEraser, FaFileSignature, FaPrint, FaSave, FaTimes } from "react-icons/fa";
import "./Violation.css";

const LETTERS = {
  FIRST_WARNING: {
    title: "FIRST WARNING",
    titleAr: "الإنذار الأول",
    reason: "Formal first warning for the recorded driving violation.",
    reasonAr: "إنذار أول رسمي بشأن مخالفة القيادة المسجلة.",
    purpose: "This first warning formally records the violation and the corrective action agreed with the driver.",
    purposeAr: "يوثق هذا الإنذار الأول المخالفة والإجراء التصحيحي المتفق عليه مع السائق.",
    showFine: false,
    consequence: "Any repeated violation may result in a final warning or further disciplinary action.",
  },
  FINAL_SEATBELT: {
    title: "FINAL WARNING - SEAT BELT VIOLATION",
    titleAr: "إنذار نهائي - مخالفة حزام الأمان",
    reason: "Not wearing the seat belt while driving.",
    reasonAr: "عدم ارتداء حزام الأمان أثناء القيادة.",
    purpose: "Any OMR 500 fine resulting from this violation, if applicable, will be passed to the concerned subcontractor.",
    purposeAr: "سيتم تحميل أي غرامة بقيمة 500 ريال عماني ناتجة عن المخالفة، إن وجدت، على المقاول المعني.",
    showFine: false,
    consequence: "A repeated violation may result in dismissal from the project.",
  },
  FINAL_NIGHT: {
    title: "FINAL WARNING - NIGHT DRIVING",
    titleAr: "إنذار نهائي - القيادة الليلية",
    reason: "Driving after daylight hours without authorization.",
    reasonAr: "القيادة الليلية دون تصريح.",
    purpose: "Any further critical IVMS or DFMS violation may result in permanent removal from the AMLS Duqm project. Any monetary penalty imposed by PDO may be passed to the responsible party.",
    purposeAr: "أي مخالفة حرجة أخرى عبر نظامي IVMS أو DFMS قد تؤدي إلى الإزالة النهائية من مشروع AMLS بالدقم، كما يجوز تحميل الجهة المسؤولة أي غرامة مالية صادرة من PDO.",
    showFine: false,
    consequence: "A repeated violation may result in dismissal from the project and applicable financial penalties.",
  },
  FINAL_MOBILE: {
    title: "FINAL WARNING - MOBILE PHONE USAGE",
    titleAr: "إنذار نهائي - استخدام الهاتف المحمول",
    reason: "DFMS distraction violation - mobile phone usage while driving.",
    reasonAr: "مخالفة تشتيت الانتباه عبر نظام DFMS - استخدام الهاتف أثناء القيادة.",
    purpose: "Any further critical IVMS or DFMS violation, including mobile phone usage, distraction, dangerous driving, failure to wear a seat belt, over-speeding or device tampering, may result in permanent removal from the AMLS Duqm project.",
    purposeAr: "أي مخالفة حرجة أخرى عبر نظامي IVMS أو DFMS، بما في ذلك استخدام الهاتف أو تشتيت الانتباه أو القيادة الخطرة أو عدم ارتداء حزام الأمان أو تجاوز السرعة أو العبث بالأجهزة، قد تؤدي إلى الإزالة النهائية من مشروع AMLS بالدقم.",
    showFine: true,
    consequence: "A repeated critical DFMS/IVMS violation may result in permanent removal from the project.",
  },
  COUNSELLING_REST: {
    title: "GENERAL VIOLATION FEEDBACK - COUNSELLING",
    titleAr: "تقرير ملاحظات المخالفة - جلسة توجيه",
    reason: "Rest violation - insufficient mandatory rest.",
    reasonAr: "مخالفة عدم أخذ الاستراحة الإلزامية الكافية.",
    purpose: "Any OMR 500 fine resulting from this violation, if applicable, will be passed to the concerned subcontractor.",
    purposeAr: "سيتم تحميل أي غرامة بقيمة 500 ريال عماني ناتجة عن المخالفة، إن وجدت، على المقاول المعني.",
    showFine: false,
    consequence: "Continued non-compliance may lead to formal disciplinary action.",
  },
};

const recommendLetter = (violation = "") => {
  const value = violation.toLowerCase();
  if (value.includes("rest")) return "COUNSELLING_REST";
  if (value.includes("seat belt") || value.includes("seatbelt")) return "FINAL_SEATBELT";
  if (value.includes("night")) return "FINAL_NIGHT";
  if (value.includes("mobile") || value.includes("distract")) return "FINAL_MOBILE";
  return "FIRST_WARNING";
};

const localISODate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const displayDate = (value) => {
  if (!value) return "—";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-GB");
};

const SignaturePad = ({ label, labelAr, value, onChange, disabled = false }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const render = () => {
      const width = Math.max(canvas.parentElement?.clientWidth || 320, 260);
      canvas.width = width;
      canvas.height = window.innerWidth <= 700 ? 190 : 145;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (value) {
        const image = new Image();
        image.onload = () => ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        image.src = value;
      }
    };
    render();
  }, [value]);

  const point = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };
  const start = (event) => {
    if (disabled) return;
    event.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = point(event);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (event) => {
    if (disabled || !drawing.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = point(event);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#132f46";
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const stop = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png", 0.72));
  };
  const clear = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div className="vletter-signature-pad">
      <div><strong>{label}</strong><span>{labelAr}</span></div>
      <canvas className={disabled ? "is-disabled" : ""} ref={canvasRef} onPointerDown={start} onPointerMove={move} onPointerUp={stop} onPointerCancel={stop} onPointerLeave={stop} />
      <button type="button" onClick={clear} disabled={disabled}><FaEraser /> Clear / مسح</button>
    </div>
  );
};

const HandleViolation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [letterOpen, setLetterOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [letter, setLetter] = useState({
    letter_type: "FIRST_WARNING",
    feedback_date: localISODate(),
    driver_explanation: "",
    preventative_action: "",
    letter_reason: "",
    consequence: "",
    driver_response: "",
    manual_remarks: "",
    counsellor_name: localStorage.getItem("name") || localStorage.getItem("username") || "",
    counsellor_designation: "",
    driver_signature: "",
    counsellor_signature: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const violationResponse = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/driver-violations/${id}`, { headers });
        const violation = violationResponse.data;
        setRecord(violation);
        const recommended = recommendLetter(violation.VIOLATION);
        setLetter((previous) => ({ ...previous, letter_type: recommended, letter_reason: LETTERS[recommended].reason, consequence: LETTERS[recommended].consequence, manual_remarks: violation.REMARKS || "" }));

        try {
          const savedResponse = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/driver-violations/${id}/action-letter`, { headers });
          const saved = savedResponse.data?.data;
          if (saved) {
            const normalized = Object.fromEntries(Object.entries(saved).map(([key, val]) => [key.toLowerCase(), val ?? ""]));
            setLetter((previous) => ({
              ...previous,
              ...normalized,
              letter_reason: normalized.letter_reason || previous.letter_reason || LETTERS[normalized.letter_type || recommended].reason,
            }));
          }
        } catch (error) {
          if (error.response?.status === 503) setMessage(error.response.data?.message || "Warning letter database setup is required.");
        }
        setLetterOpen(true);
      } catch (error) {
        console.error("Error loading violation:", error);
        setMessage("Failed to load violation details.");
      }
    };
    load();
  }, [id]);

  const updateLetter = (event) => setLetter((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  const changeType = (event) => {
    const letter_type = event.target.value;
    setLetter((previous) => ({ ...previous, letter_type, letter_reason: LETTERS[letter_type].reason, consequence: LETTERS[letter_type].consequence }));
  };

  const saveLetter = async (resolveAfter = false) => {
    if (record && (record.RESOLVED === "Y" || String(record.VIOLATION_STATUS || "").toUpperCase() === "RESOLVED")) {
      setMessage("This violation is already resolved. The completed letter is view only.");
      return;
    }
    if (!letter.counsellor_name.trim()) {
      setMessage("Counsellor name is required.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${process.env.REACT_APP_BASE_API_URL}/driver-violations/${id}/action-letter`, {
        ...letter,
      }, { headers });
      if (resolveAfter) {
        await axios.patch(`${process.env.REACT_APP_BASE_API_URL}/driver-violations/${id}/resolve`, {
          counselled_by: letter.counsellor_name,
          action_taken: LETTERS[letter.letter_type].title,
          sendNotification: true,
        }, { headers });
        setMessage("Warning/counselling letter completed and violation resolved successfully.");
        setTimeout(() => navigate("/driver-violations"), 1600);
      } else {
        setMessage("Warning letter saved successfully.");
      }
    } catch (error) {
      console.error("Error saving warning letter:", error);
      setMessage(error.response?.data?.message || "Failed to save warning letter.");
    } finally {
      setSaving(false);
    }
  };

  const template = LETTERS[letter.letter_type];
  const isResolved = record && (record.RESOLVED === "Y" || String(record.VIOLATION_STATUS || "").toUpperCase() === "RESOLVED");
  return (
    <main className="handle-violation-container vletter-page">
      <header className="vletter-page-header">
        <button onClick={() => navigate("/driver-violations")} aria-label="Back"><FaArrowLeft /></button>
        <div><h1>Handle Driver Violation</h1><p>Complete the required warning or counselling letter</p></div>
      </header>
      {message && <div className={`violation-message ${message.includes("success") ? "success" : "warning"}`}>{message}</div>}
      {record && <section className="vletter-summary">
        <div><span>Driver</span><strong>{record.DRIVER_NAME || "—"}</strong></div>
        <div><span>Vehicle</span><strong>{record.SCHEDULED_VEHICLE || "—"}</strong></div>
        <div><span>Violation</span><strong>{record.VIOLATION || "—"}</strong></div>
        <div><span>Date</span><strong>{displayDate(record.VIOLATION_TIME)}</strong></div>
      </section>}
      <section className="vletter-launch-card">
        <FaFileSignature />
        <div><h2>{template.title}</h2><p>{template.titleAr}</p></div>
        <button onClick={() => setLetterOpen(true)}>Open warning letter</button>
      </section>

      {letterOpen && record && <div className="vletter-overlay" role="dialog" aria-modal="true">
        <div className="vletter-modal">
          <div className="vletter-modal-toolbar no-print">
            <button onClick={() => setLetterOpen(false)}><FaTimes /> Close</button>
            <button onClick={() => window.print()}><FaPrint /> Print</button>
          </div>
          {message && <div className={`vletter-modal-message no-print ${message.includes("success") ? "success" : "warning"}`}>{message}</div>}
          {isResolved && <div className="vletter-resolved-banner no-print"><FaCheckCircle /> Resolved — completed letter is view only</div>}
          <article className="vletter-document">
            <header className="vletter-document-header">
              <img className="vletter-letter-logo" src="/almadinalogo.jpeg" alt="Al Madina Logistics" />
              <div><small>DRIVER SAFETY & COMPLIANCE</small><h1>{template.title}</h1><h2 dir="rtl">{template.titleAr}</h2></div>
            </header>
            <fieldset className="vletter-lockable" disabled={isResolved}>

            <div className="vletter-type-row no-print">
              <label>Letter type / نوع الخطاب</label>
              <select value={letter.letter_type} onChange={changeType}>
                {Object.entries(LETTERS).map(([key, value]) => <option key={key} value={key}>{value.title}</option>)}
              </select>
            </div>

            <section className="vletter-details-grid">
              <label><span>Name & vehicle / اسم السائق والمركبة</span><input readOnly value={`${record.DRIVER_NAME || ""} - ${record.SCHEDULED_VEHICLE || ""}`} /></label>
              <label><span>Violation date / تاريخ المخالفة</span><input readOnly value={displayDate(record.VIOLATION_TIME)} /></label>
              <label><span>Feedback date / تاريخ التنبيه</span><input type="date" name="feedback_date" value={letter.feedback_date} onChange={updateLetter} /></label>
              <label><span>Violation / المخالفة</span><input readOnly value={record.VIOLATION || ""} /></label>
            </section>

            <section className="vletter-purpose">
              <h3>Purpose of the Final Warning/Counselling / سبب الإنذار أو التوجيه</h3>
              <p>{template.purpose}</p><p dir="rtl">{template.purposeAr}</p>
              {template.showFine && <><p className="vletter-fine">Any applicable OMR 500 fine resulting from the violation may be passed to the concerned subcontractor.</p><p className="vletter-fine" dir="rtl">يجوز تحميل المقاول المعني أي غرامة قدرها 500 ريال عماني ناتجة عن المخالفة، إن وجدت.</p></>}
            </section>

            <section className="vletter-form-table">
              <label><span>{letter.letter_type === "COUNSELLING_REST" ? "Reason for counselling" : "Reason for warning letter"}<b dir="rtl">{letter.letter_type === "COUNSELLING_REST" ? "سبب التوجيه" : "سبب الإنذار"}</b></span><textarea name="letter_reason" value={letter.letter_reason} onChange={updateLetter} /></label>
              <label><span>Why was the violation committed?<b dir="rtl">سبب قيام السائق بالمخالفة</b></span><textarea name="driver_explanation" value={letter.driver_explanation} onChange={updateLetter} /></label>
              <label><span>Preventative action - The Way Forward<b dir="rtl">الطريق إلى الأمام وكيفية التجنب</b></span><textarea name="preventative_action" value={letter.preventative_action} onChange={updateLetter} /></label>
              <label><span>Consequence of not improving<b dir="rtl">عواقب تكرار المخالفة</b></span><textarea name="consequence" value={letter.consequence} onChange={updateLetter} /></label>
              <label><span>Response by driver<b dir="rtl">رد السائق</b></span><textarea name="driver_response" value={letter.driver_response} onChange={updateLetter} /></label>
              <label><span>Remarks<b dir="rtl">الملاحظات</b></span><textarea name="manual_remarks" value={letter.manual_remarks} onChange={updateLetter} placeholder="Editable remarks for this warning letter" /></label>
            </section>

            <section className="vletter-counsellor-grid">
              <label><span>Counsellor name / اسم المستشار</span><input name="counsellor_name" value={letter.counsellor_name} onChange={updateLetter} /></label>
              <label><span>Designation / الوظيفة</span><input name="counsellor_designation" value={letter.counsellor_designation} onChange={updateLetter} /></label>
            </section>

            <section className="vletter-signatures">
              <SignaturePad disabled={isResolved} label="Driver signature (optional)" labelAr="توقيع السائق (اختياري)" value={letter.driver_signature} onChange={(value) => setLetter((p) => ({ ...p, driver_signature: value }))} />
              <SignaturePad disabled={isResolved} label="Counsellor signature (optional)" labelAr="توقيع المستشار (اختياري)" value={letter.counsellor_signature} onChange={(value) => setLetter((p) => ({ ...p, counsellor_signature: value }))} />
            </section>
            </fieldset>
          </article>
          <footer className="vletter-actions no-print">
            <button disabled={saving || isResolved} onClick={() => saveLetter(false)}><FaSave /> {isResolved ? "Letter saved" : saving ? "Saving..." : "Save letter"}</button>
            <button disabled={saving || isResolved} onClick={() => saveLetter(true)}><FaCheckCircle /> {isResolved ? "Violation resolved" : "Complete & resolve violation"}</button>
          </footer>
        </div>
      </div>}
    </main>
  );
};

export default HandleViolation;
